import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useIsFetching } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { Stack, Typography } from "@mui/material"
import { useField } from "formik"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { DateSelectArg, EventClickArg, EventDropArg, EventHoveringArg } from "@fullcalendar/core"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { ConfigQuery } from "../../../createOrderApi"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import { TimelineResourceGroupLabelContent, TimelineResourceLabelContent } from "../calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { AbandonBookingDialog } from "../../AbandonBookingDialog"
import { forceFullRevert, isInPast, toFloatingIsoMinutesFromJsDate } from "../calendar/helpers"

import { BookPeriodMessageDialog } from "./BookPeriodMessageDialog"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { createNewEvent, DEFAULT_MINIMUM_DURATION_MINUTES, isCanEditExistOrder, isLocalEvent } from "./helpers"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { HoverTooltipLayer, ResourceBookingCalendarHoveredEvent } from "./tooltip/HoverTooltipLayer"
import { useResourceHelpers } from "./timelineHooks"
import { useRefreshCalendar } from "../context/RefreshCalendarProvider"
import { ResourceLabel } from "./ResourceLabel"
import { useTimelineContext } from "../context/TimelineContextProvider"

const DAY_OFFSETS = [0, 1, 2] as const

type ResourceBookingCalendarProps = Readonly<{
    groupImagesMap: Map<string, string>
    handleChangeOrderContext: (orderId: string) => void
}>

export const ResourceBookingCalendar = memo(function ResourceBookingCalendar({
    groupImagesMap,
    handleChangeOrderContext,
}: ResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const isDraggingRef = useRef(false)
    const setHoverRef = useRef<(event: ResourceBookingCalendarHoveredEvent | null) => void>(() => {})

    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const [showBookPeriodDialogMessage, setShowBookPeriodDialogMessage] = useState(false)
    const [confirmAbandonDialogState, setConfirmAbandonDialogState] = useState<{ id: string; title: string } | null>(
        null,
    )
    const [timeChangeDialogState, setTimeChangeDialogState] = useState<{
        resourceId: string
        newStart: string
        newEnd: string
    } | null>(null)

    const { setCalendarRef } = useRefreshCalendar()
    const {
        timelineResources,
        resourceBufferMap,
        allServiceCenterIdsForEvents,
        userId,
        localEventUpdateId,
        isBookPeriodMode,
        isOutlookFlow,
    } = useTimelineContext()
    const { draftEvent: localEvent } = useResourceBookingDraft()
    const { createSingleDayFetcher, handleChangeLocalEvent, isResourceAvailable } = useResourceHelpers({})
    const [{ value: selectedDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const activeFetches = useIsFetching({
        queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY, userId],
    })
    const isLoadingEvents = activeFetches > 0

    const localEventsSource = useMemo(
        () => ({
            id: "local-events",
            events: localEvent ? [localEvent] : [],
        }),
        [localEvent],
    )

    const serverEventsSource = useMemo(
        () =>
            allServiceCenterIdsForEvents.flatMap((id) =>
                DAY_OFFSETS.map((dayOffset) => ({
                    id: `source-${id}-day${dayOffset}`,
                    events: createSingleDayFetcher(id, dayOffset),
                })),
            ),
        [allServiceCenterIdsForEvents, createSingleDayFetcher],
    )

    const eventSources = useMemo(() => {
        return [localEventsSource, ...serverEventsSource]
    }, [serverEventsSource, localEventsSource])

    const handleSelect = ({ startStr, endStr, resource }: DateSelectArg) => {
        if (!resource) return

        if (isBookPeriodMode) {
            setShowBookPeriodDialogMessage(true)
            return
        }

        const startDt = dateTimeFactory.fromISO(startStr)
        const endDtRaw = dateTimeFactory.fromISO(endStr)
        const durationMinutes = endDtRaw.diff(startDt, "minutes").minutes

        // FullCalendar uses `snapDuration` as the selection length on simple click.
        // With fine-grained snapping (e.g. 1 minute), this would otherwise create 1-minute events.
        const endDt =
            Number.isFinite(durationMinutes) && durationMinutes <= 1
                ? startDt.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })
                : endDtRaw

        const start = dateTimeFactory.toApiISOMinutesFormat(startDt)
        const end = dateTimeFactory.toApiISOMinutesFormat(endDt)

        const resourceId = resource.id
        const currentResources = localEvent?.resourceIds || []

        const hasOutlookTimeGuard = isOutlookFlow && localEvent && currentResources.length === 0
        if (hasOutlookTimeGuard) {
            const isTimeDifferent = localEvent.start !== start || localEvent.end !== end

            if (isTimeDifferent) {
                const updatedResourceIds = [resourceId]

                const isFreeAtNewTime = isResourceAvailable(start, end, updatedResourceIds, localEvent.id)

                if (!isFreeAtNewTime) {
                    enqueueSnackbar({
                        variant: "warning",
                        message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                    })
                    return
                }

                setTimeChangeDialogState({
                    resourceId,
                    newStart: start,
                    newEnd: end,
                })
                return
            }
        }

        if (localEvent) {
            const updatedResourceIds = currentResources.includes(resourceId)
                ? currentResources
                : [...currentResources, resourceId]

            if (!isResourceAvailable(start, end, updatedResourceIds, localEvent.id)) {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-error-select"),
                })
                return
            }

            if (currentResources.includes(resourceId)) {
                handleChangeLocalEvent({
                    ...localEvent,
                    start,
                    end,
                })

                return
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: [...currentResources, resourceId],
                    start,
                    end,
                })

                return
            }
        }

        if (!isResourceAvailable(start, end, [resourceId])) {
            return
        }

        handleChangeLocalEvent(createNewEvent(resourceId, start, end))
    }

    const handleEventDrop = (info: EventDropArg) => {
        if (isLoadingEvents) {
            forceFullRevert(info)
            return
        }
        const { event, oldResource } = info
        const { id, extendedProps, start, end } = event

        if (isBookPeriodMode && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }

        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newResourceIds = event.getResources().map((res) => res.id)
        const newStart = toFloatingIsoMinutesFromJsDate(dateTimeFactory, start!)
        const newEnd = toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!)

        if (!isResourceAvailable(newStart, newEnd, newResourceIds, id)) {
            forceFullRevert(info)
            return
        }

        if (localEvent) {
            const currentResourceIds = localEvent.resourceIds
            const draggedResourceId = oldResource?.id

            const updatedResourceIds = draggedResourceId
                ? currentResourceIds
                      .filter((id) => id !== draggedResourceId)
                      .concat(newResourceIds.filter((id) => !currentResourceIds.includes(id)))
                : [...new Set([...currentResourceIds, ...newResourceIds])]

            const updatedEvent = {
                ...localEvent,
                resourceIds: updatedResourceIds,
                start: newStart,
                end: newEnd,
            }

            handleChangeLocalEvent(updatedEvent)
        }
    }

    const handleEventClick = (info: EventClickArg) => {
        const eventId = info.event.id
        const clickedElement = info.jsEvent.target as HTMLElement
        const clickedResourceId = clickedElement?.closest("[data-resource-id]")?.getAttribute("data-resource-id")

        if (!clickedResourceId) {
            return
        }

        if (localEvent && localEvent.id === eventId) {
            const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== clickedResourceId)

            if (updatedResourceIds.length === 0) {
                if (isOutlookFlow) {
                    handleChangeLocalEvent({ ...localEvent, resourceIds: [] })
                } else {
                    handleChangeLocalEvent(null)
                }
            } else {
                handleChangeLocalEvent({
                    ...localEvent,
                    resourceIds: updatedResourceIds,
                })
            }
        }
    }

    const handleEventResize = (info: EventResizeDoneArg) => {
        const { id, start, end, extendedProps } = info.event

        if (isBookPeriodMode && isLocalEvent(id)) {
            setShowBookPeriodDialogMessage(true)
            forceFullRevert(info)
            return
        }
        if (!isLocalEvent(id)) {
            if (isCanEditExistOrder(info)) {
                if (localEvent) {
                    setConfirmAbandonDialogState({ id, title: extendedProps["subject"] })
                } else handleChangeOrderContext(id)
            } else {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-timeline-permission-error"),
                })
            }
            forceFullRevert(info)
            return
        }

        const newStart = toFloatingIsoMinutesFromJsDate(dateTimeFactory, start!)
        const newEnd = toFloatingIsoMinutesFromJsDate(dateTimeFactory, end!)

        const resourceIds = info.event.getResources().map((res) => res.id)

        if (!isResourceAvailable(newStart, newEnd, resourceIds, id)) {
            forceFullRevert(info)
            return
        }

        handleChangeLocalEvent({
            ...localEvent!,
            start: newStart,
            end: newEnd,
        })
    }

    const resourceLabelContent = useCallback(
        ({ resource }: TimelineResourceLabelContent) => <ResourceLabel resource={resource} />,
        [],
    )

    const resourceGroupLabelContent = useCallback(
        ({ groupValue }: TimelineResourceGroupLabelContent) => (
            <ResourceCalendarGroupContent
                title={groupValue}
                groupImagesMap={groupImagesMap}
                onImageClick={openImageModal}
            />
        ),
        [groupImagesMap, openImageModal],
    )

    const handleEventHover = useCallback(
        (info: EventHoveringArg | null) => {
            if (isDraggingRef.current) return
            if (!info || !info.el) {
                setHoverRef.current(null)
                return
            }

            const { el } = info
            const { id, start, end, extendedProps } = info.event

            const rect = el.getBoundingClientRect()

            const top = rect.top + window.scrollY - 8
            const left = rect.left + window.scrollX + rect.width / 2

            const checkEvent = isLocalEvent(id)
            setHoverRef.current({
                id,
                top,
                left,
                local: checkEvent,
                start: dateTimeFactory.formatDateTime(dateTimeFactory.fromJSDate(start!)),
                end: dateTimeFactory.formatDateTime(dateTimeFactory.fromJSDate(end!)),
                host: checkEvent ? undefined : extendedProps["host"],
                subject: checkEvent ? undefined : extendedProps["subject"],
            })
        },
        [dateTimeFactory],
    )

    const handleCloseConfirmAbandonDialog = () => setConfirmAbandonDialogState(null)
    const handleConfirmAbandonDialogClick = () => {
        confirmAbandonDialogState && handleChangeOrderContext(confirmAbandonDialogState.id)
        handleCloseConfirmAbandonDialog()
    }

    const handleKeepOriginalTime = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        if (isInPast(localEvent.start) || isInPast(localEvent.end)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-past-booking-attempt-message"),
            })
            return
        }

        const { resourceId } = timeChangeDialogState

        const originalStart = localEvent.start
        const originalEnd = localEvent.end

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(originalStart, originalEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: originalStart,
            end: originalEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    const handleConfirmTimeChange = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        const { resourceId, newStart, newEnd } = timeChangeDialogState

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(newStart, newEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: newStart,
            end: newEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    return (
        <>
            <StandardTimelineCalendar
                ref={setCalendarRef}
                viewMode="daysGrid"
                localEvents={localEventsSource?.events}
                eventSources={eventSources}
                isLoadingEvents={isLoadingEvents}
                resources={timelineResources}
                resourceBufferMap={resourceBufferMap}
                handleSelect={handleSelect}
                handleEventDrop={handleEventDrop}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
                handleEventResize={handleEventResize}
                handleEventHover={handleEventHover}
                isBookedPeriod={isBookPeriodMode}
                isResourceAvailable={isResourceAvailable}
                selectedDeliveries={selectedDeliveries}
                isDraggingRef={isDraggingRef}
                isUpdateMode={!!localEventUpdateId}
            />

            <HoverTooltipLayer isDraggingRef={isDraggingRef} setHoverRef={setHoverRef} />

            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
            {showBookPeriodDialogMessage && (
                <BookPeriodMessageDialog onClose={() => setShowBookPeriodDialogMessage(false)} />
            )}
            {confirmAbandonDialogState && (
                <LocalizedStrict
                    id={
                        localEventUpdateId
                            ? "create-order-change-current-event-dialog"
                            : "create-order-change-current-event-create-dialog"
                    }
                    attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                    vars={{ orderDetails: `#${confirmAbandonDialogState.id}, ${confirmAbandonDialogState.title}` }}
                >
                    <AbandonBookingDialog
                        title="Confirm booking"
                        message="Are you sure you want to reset changes and update another booking?"
                        handleSubmitClick={handleConfirmAbandonDialogClick}
                        handleCancelClick={handleCloseConfirmAbandonDialog}
                        buttonLabelSubmit="Confirm"
                        buttonLabelCancel="Cancel"
                        open={!!confirmAbandonDialogState}
                        onClose={handleCloseConfirmAbandonDialog}
                    />
                </LocalizedStrict>
            )}
            {timeChangeDialogState && (
                <LocalizedStrict
                    id="create-order-change-time-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                >
                    <AbandonBookingDialog
                        title="Change booking time?"
                        message={
                            <Stack gap="1rem">
                                <Typography variant="body1">
                                    <LocalizedStrict id="create-order-change-time-dialog-message-top">
                                        <>Your Outlook meeting request is scheduled for:</>
                                    </LocalizedStrict>
                                    <Typography fontWeight={500}>
                                        {localEvent
                                            ? dateTimeFactory.formatCompactPeriodFromDateTime(
                                                  dateTimeFactory.fromISO(localEvent.start),
                                                  dateTimeFactory.fromISO(localEvent.end),
                                              )
                                            : ""}
                                    </Typography>
                                </Typography>
                                <Typography variant="body1">
                                    <LocalizedStrict id="create-order-change-time-dialog-message-bottom">
                                        <>Do you want to change booking time to:</>
                                    </LocalizedStrict>
                                    <Typography fontWeight={500}>
                                        {timeChangeDialogState
                                            ? dateTimeFactory.formatCompactPeriodFromDateTime(
                                                  dateTimeFactory.fromISO(timeChangeDialogState.newStart),
                                                  dateTimeFactory.fromISO(timeChangeDialogState.newEnd),
                                              )
                                            : ""}
                                    </Typography>
                                </Typography>
                            </Stack>
                        }
                        handleSubmitClick={handleConfirmTimeChange}
                        handleCancelClick={handleKeepOriginalTime}
                        buttonLabelSubmit="Use new time"
                        buttonLabelCancel="Keep original time"
                        open={!!timeChangeDialogState}
                        onClose={handleKeepOriginalTime}
                    />
                </LocalizedStrict>
            )}
        </>
    )
})
